import React, { useState, useEffect } from 'react';
import { 
  FiUser, FiEdit2, FiSave, FiX, FiMail, FiPhone, FiMapPin, 
  FiCalendar, FiLink, FiCopy, FiCheck, FiAward, FiUsers, 
  FiFileText, FiGift, FiHash, FiInstagram, FiFacebook, FiYoutube,
  FiTrendingUp, FiBook, FiMessageSquare
} from 'react-icons/fi';
import { FaLine, FaTiktok } from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';
import { agentAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TrainingManager from '../../components/common/TrainingManager';
import PolicyManager from '../../components/common/PolicyManager';
import CoachingManager from '../../components/common/CoachingManager';

// Helper Component for Sections
const ProfileSection = ({ title, icon: Icon, isEditing, onEdit, onSave, onCancel, children, hideEdit = false, customActions = null }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100 h-full">
    <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
       <h4 className="font-semibold text-gray-900 flex items-center">
          <SafeIcon icon={Icon} className="mr-2 text-indigo-500" />
          {title}
       </h4>
       {!hideEdit && (
         <div className="flex items-center">
            {customActions}
            {isEditing ? (
               <div className="flex space-x-2 ml-2">
                  <button 
                    onClick={onCancel} 
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
                  >
                    <SafeIcon icon={FiX} className="w-3 h-3 mr-1" /> ยกเลิก
                  </button>
                  {onSave && (
                    <button 
                      onClick={onSave} 
                      className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 flex items-center"
                    >
                      <SafeIcon icon={FiSave} className="w-3 h-3 mr-1" /> บันทึก
                    </button>
                  )}
               </div>
            ) : (
               <button 
                 onClick={onEdit} 
                 className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
               >
                 <SafeIcon icon={FiEdit2} className="w-3 h-3 mr-1" /> แก้ไข
               </button>
            )}
         </div>
       )}
    </div>
    <div className="p-6">
       {children}
    </div>
  </div>
);

const LineProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null); // 'general', 'contact', 'work', 'social', 'additional', 'policy', 'training', 'coaching'
  const [formData, setFormData] = useState({});
  
  // Link Generator State
  const [linkLoading, setLinkLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await agentAPI.getMyProfile();
      setProfile(data);
      setFormData(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('ไม่สามารถดึงข้อมูลโปรไฟล์ได้');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEdit = (section) => {
    setFormData(profile); // Reset form data to match current profile before editing
    setEditingSection(section);
  };

  const handleCancel = () => {
    setEditingSection(null);
    setFormData(profile);
  };

  const handleSave = async () => {
    try {
      const dataToSave = {
        ...formData,
        monthlySales: formData.monthlySales ? parseFloat(formData.monthlySales) : 0,
        yearlySales: formData.yearlySales ? parseFloat(formData.yearlySales) : 0,
      };

      await agentAPI.updateAgent(profile.id, dataToSave);
      setProfile(dataToSave);
      setEditingSection(null);
      toast.success('บันทึกข้อมูลเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('ไม่สามารถบันทึกข้อมูลได้');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  const generateOneTimeLink = async () => {
    setLinkLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      
      const response = await fetch(`${API_BASE_URL}/auth/generate-one-time-login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate login link');
      }

      const data = await response.json();
      setGeneratedLink(data.loginLink);
    } catch (error) {
      console.error('Error generating one-time login link:', error);
      toast.error('ไม่สามารถสร้างลิงก์เข้าสู่ระบบได้');
    } finally {
      setLinkLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('คัดลอกลิงก์แล้ว');
    } catch (error) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = generatedLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('คัดลอกลิงก์แล้ว');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!profile) return <div className="p-6 text-center text-gray-500">ไม่พบข้อมูลโปรไฟล์</div>;

  const userImageUrl = profile.user?.pictureUrl;
  const fullName = `${profile.firstName} ${profile.lastName}`;
  const downlineCount = profile.downlines?.length || 0;
  const policyCount = profile.policies?.length || 0;
  const teamPolicyCount = profile.downlines?.reduce((sum, downline) => sum + (downline.policies?.length || 0), 0) || 0;
  const totalPolicies = policyCount + teamPolicyCount;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">ข้อมูลส่วนตัว</h1>
        <p className="text-gray-600">จัดการข้อมูลโปรไฟล์ตัวแทนของคุณ</p>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-lg shadow mb-6 p-6 border border-gray-100 flex items-center">
         <div className="flex-shrink-0 h-20 w-20">
            {userImageUrl ? (
              <img className="h-20 w-20 rounded-full object-cover border-4 border-indigo-50" src={userImageUrl} alt={fullName} />
            ) : (
              <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center border-4 border-indigo-50">
                <SafeIcon icon={FiUser} className="h-10 w-10 text-indigo-500" />
              </div>
            )}
         </div>
         <div className="ml-4">
            <h3 className="text-xl font-bold text-gray-900">{fullName}</h3>
            <p className="text-indigo-600 font-medium">{profile.position || 'ตัวแทน'}</p>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <SafeIcon icon={FiHash} className="mr-1 h-3 w-3" />
              {profile.agentCode || '-'}
            </div>
         </div>
      </div>
{/* Stats Section */}
       <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
         <div className="bg-white p-2 sm:p-6 rounded-lg shadow flex flex-col sm:flex-row items-start sm:items-center justify-between border-l-4 border-indigo-500">
           <div className="overflow-hidden w-full">
             <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0 sm:mb-1 truncate">ลูกทีมทั้งหมด</p>
             <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{downlineCount} คน</p>
           </div>
           <div className="hidden sm:block bg-indigo-100 p-3 rounded-full flex-shrink-0">
              <SafeIcon icon={FiUsers} className="w-6 h-6 text-indigo-600" />
           </div>
         </div>
         <div className="bg-white p-2 sm:p-6 rounded-lg shadow flex flex-col sm:flex-row items-start sm:items-center justify-between border-l-4 border-green-500">
           <div className="overflow-hidden w-full">
             <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0 sm:mb-1 truncate">กรมธรรม์ส่วนตัว</p>
             <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{policyCount} ฉบับ</p>
           </div>
           <div className="hidden sm:block bg-green-100 p-3 rounded-full flex-shrink-0">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-green-600" />
           </div>
         </div>
         <div className="bg-white p-2 sm:p-6 rounded-lg shadow flex flex-col sm:flex-row items-start sm:items-center justify-between border-l-4 border-blue-500">
           <div className="overflow-hidden w-full">
             <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0 sm:mb-1 truncate">กรมธรรม์ทั้งทีม (รวมส่วนตัว)</p>
             <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{totalPolicies} ฉบับ</p>
           </div>
           <div className="hidden sm:block bg-blue-100 p-3 rounded-full flex-shrink-0">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600" />
           </div>
         </div>
       </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* 1. General Info */}
      <ProfileSection 
        title="ข้อมูลทั่วไป" 
        icon={FiUser} 
        isEditing={editingSection === 'general'}
        onEdit={() => handleEdit('general')}
        onSave={handleSave}
        onCancel={handleCancel}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ชื่อจริง</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">นามสกุล</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.lastName}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ชื่อเล่น</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.nickname || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">วันเกิด</label>
              {editingSection === 'general' ? (
                <input
                  type="date"
                  name="birthDate"
                  value={formatDateForInput(formData.birthDate)}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <div className="flex items-center">
                  <SafeIcon icon={FiGift} className="w-3 h-3 text-gray-400 mr-2" />
                  <p className="text-sm font-medium text-gray-900">{formatDate(profile.birthDate)}</p>
                </div>
              )}
            </div>
        </div>
      </ProfileSection>

      {/* 2. Contact Info */}
      <ProfileSection 
        title="การติดต่อ" 
        icon={FiPhone} 
        isEditing={editingSection === 'contact'}
        onEdit={() => handleEdit('contact')}
        onSave={handleSave}
        onCancel={handleCancel}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">เบอร์โทรศัพท์</label>
            {editingSection === 'contact' ? (
              <input
                type="tel"
                name="phone"
                value={formData.phone || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <div className="flex items-center">
                <SafeIcon icon={FiPhone} className="w-3 h-3 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-900">{profile.phone || '-'}</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Line ID</label>
            {editingSection === 'contact' ? (
              <input
                type="text"
                name="lineId"
                value={formData.lineId || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <div className="flex items-center">
                <SafeIcon icon={FaLine} className="w-3 h-3 text-green-500 mr-2" />
                <p className="text-sm font-medium text-gray-900">{profile.lineId || '-'}</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">พื้นที่รับผิดชอบ</label>
            {editingSection === 'contact' ? (
              <input
                type="text"
                name="area"
                value={formData.area || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <div className="flex items-center">
                <SafeIcon icon={FiMapPin} className="w-3 h-3 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-900">{profile.area || '-'}</p>
              </div>
            )}
          </div>
        </div>
      </ProfileSection>

      {/* 3. Work & License */}
      <ProfileSection 
        title="ข้อมูลงานและใบอนุญาต" 
        icon={FiAward} 
        isEditing={editingSection === 'work'}
        onEdit={() => handleEdit('work')}
        onSave={handleSave}
        onCancel={handleCancel}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">ตำแหน่ง</label>
                {editingSection === 'work' ? (
                  <input
                    type="text"
                    name="position"
                    value={formData.position || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{profile.position || '-'}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">วันที่เข้าร่วมงาน</label>
                {editingSection === 'work' ? (
                  <input
                    type="date"
                    name="joinDate"
                    value={formatDateForInput(formData.joinDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="w-3 h-3 text-gray-400 mr-2" />
                    <p className="text-sm font-medium text-gray-900">{formatDate(profile.joinDate)}</p>
                  </div>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">เลขที่ใบอนุญาต</label>
                <p className="text-sm font-medium text-gray-900">{profile.agentCode || '-'}</p>
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">ระดับใบอนุญาต</label>
                {editingSection === 'work' ? (
                  <input
                    type="text"
                    name="licenseLevel"
                    value={formData.licenseLevel || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{profile.licenseLevel || '-'}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">วันที่เริ่มใบอนุญาต</label>
                {editingSection === 'work' ? (
                  <input
                    type="date"
                    name="licenseStartDate"
                    value={formatDateForInput(formData.licenseStartDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{formatDate(profile.licenseStartDate)}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">วันหมดอายุใบอนุญาต</label>
                {editingSection === 'work' ? (
                  <input
                    type="date"
                    name="licenseExpiryDate"
                    value={formatDateForInput(formData.licenseExpiryDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{formatDate(profile.licenseExpiryDate)}</p>
                )}
             </div>
        </div>
      </ProfileSection>

      {/* 4. Social & Interests */}
      <ProfileSection 
        title="โซเชียลมีเดียและความสนใจ" 
        icon={FiInstagram} 
        isEditing={editingSection === 'social'}
        onEdit={() => handleEdit('social')}
        onSave={handleSave}
        onCancel={handleCancel}
      >
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Facebook</label>
                {editingSection === 'social' ? (
                  <input
                    type="text"
                    name="facebook"
                    value={formData.facebook || ''}
                    onChange={handleInputChange}
                    placeholder="Link or Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                     <SafeIcon icon={FiFacebook} className="w-3 h-3 text-blue-600 mr-2" />
                     <p className="text-sm font-medium text-gray-900">{profile.facebook || '-'}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">TikTok</label>
                {editingSection === 'social' ? (
                  <input
                    type="text"
                    name="tiktok"
                    value={formData.tiktok || ''}
                    onChange={handleInputChange}
                     placeholder="Link or Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                     <SafeIcon icon={FaTiktok} className="w-3 h-3 text-black mr-2" />
                     <p className="text-sm font-medium text-gray-900">{profile.tiktok || '-'}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Instagram</label>
                {editingSection === 'social' ? (
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram || ''}
                    onChange={handleInputChange}
                     placeholder="Link or Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                     <SafeIcon icon={FiInstagram} className="w-3 h-3 text-pink-600 mr-2" />
                     <p className="text-sm font-medium text-gray-900">{profile.instagram || '-'}</p>
                  </div>
                )}
              </div>
              
              <div className="col-span-full">
                 <label className="block text-xs font-medium text-gray-500 mb-1">แรงจูงใจ / เป้าหมาย</label>
                 {editingSection === 'social' ? (
                  <textarea
                    name="motivation"
                    value={formData.motivation || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                 ) : (
                   <p className="text-sm font-medium text-gray-900">{profile.motivation || '-'}</p>
                 )}
              </div>
               <div className="col-span-full">
                 <label className="block text-xs font-medium text-gray-500 mb-1">ความสนใจ</label>
                 {editingSection === 'social' ? (
                  <textarea
                    name="interest"
                    value={formData.interest || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                 ) : (
                   <p className="text-sm font-medium text-gray-900">{profile.interest || '-'}</p>
                 )}
              </div>
           </div>
      </ProfileSection>

      {/* 5. Additional Info */}
      <ProfileSection 
        title="ข้อมูลเพิ่มเติม" 
        icon={FiTrendingUp} 
        isEditing={editingSection === 'additional'}
        onEdit={() => handleEdit('additional')}
        onSave={handleSave}
        onCancel={handleCancel}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ยอดขายรายเดือน</label>
              {editingSection === 'additional' ? (
                <input
                  type="number"
                  name="monthlySales"
                  value={formData.monthlySales || ''}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {profile.monthlySales ? Number(profile.monthlySales).toLocaleString() : '-'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ยอดขายรายปี</label>
              {editingSection === 'additional' ? (
                <input
                  type="number"
                  name="yearlySales"
                  value={formData.yearlySales || ''}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {profile.yearlySales ? Number(profile.yearlySales).toLocaleString() : '-'}
                </p>
              )}
            </div>
             <div className="col-span-full">
               <label className="block text-xs font-medium text-gray-500 mb-1">บันทึกเพิ่มเติม</label>
               {editingSection === 'additional' ? (
                <textarea
                  name="note"
                  value={formData.note || ''}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
               ) : (
                 <p className="text-sm font-medium text-gray-900 whitespace-pre-line">{profile.note || '-'}</p>
               )}
            </div>
        </div>
      </ProfileSection>

      {/* 6. Policy Section (Dedicated) */}
      <ProfileSection
      title="กรมธรรม์ของฉัน" 
        icon={FiFileText}
        isEditing={editingSection === 'policy'}
        onEdit={() => setEditingSection('policy')}
        onCancel={() => setEditingSection(null)}
        // No save button for policy section as it manages its own state
        hideEdit={false}
        customActions={editingSection === 'policy' && (
          <span className="text-xs text-gray-500 mr-2">กำลังแก้ไข...</span>
        )}
      >
        <PolicyManager
          agentId={profile.id}
          isEditable={editingSection === 'policy'}
          showTitle={true}
        />
      </ProfileSection>

      {/* 7. Training Section (Dedicated) */}
      <ProfileSection
       title="การฝึกอบรมของฉัน"
        icon={FiBook}
        isEditing={editingSection === 'training'}
        onEdit={() => setEditingSection('training')}
        onCancel={() => setEditingSection(null)}
        // No save button for training section as it manages its own state
        hideEdit={false} 
        customActions={editingSection === 'training' && (
           <span className="text-xs text-gray-500 mr-2">กำลังแก้ไข...</span>
        )}
      >
        <TrainingManager 
          agentId={profile.id} 
          isEditable={editingSection === 'training'} 
          showTitle={true}
        />
      </ProfileSection>

      {/* 8. Coaching Section (Dedicated) */}
      <ProfileSection
       title="การได้รับคำปรึกษาของฉัน"
        icon={FiMessageSquare}
        isEditing={editingSection === 'coaching'}
        onEdit={() => setEditingSection('coaching')}
        onCancel={() => setEditingSection(null)}
        hideEdit={false} 
        customActions={editingSection === 'coaching' && (
           <span className="text-xs text-gray-500 mr-2">กำลังแก้ไข...</span>
        )}
      >
        <CoachingManager 
          agentId={profile.id} 
          isEditable={editingSection === 'coaching'} 
          showTitle={true}
        />
      </ProfileSection>
      </div>

       

      {/* Web Access Link Generator */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">เข้าสู่ระบบผ่านเว็บ</h3>
          <p className="text-gray-600 mb-4">
            สร้างลิงก์สำหรับเข้าใช้งานระบบผ่านเว็บเบราว์เซอร์ ลิงก์มีอายุ 1 ชั่วโมงและใช้งานได้ครั้งเดียว
          </p>
          
          {generatedLink ? (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <p className="text-sm text-gray-600 mb-2">ลิงก์เข้าสู่ระบบ:</p>
                <p className="text-sm text-gray-800 break-all font-mono bg-white p-2 rounded">
                  {generatedLink}
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={copyToClipboard}
                  className="flex-1 flex items-center justify-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md"
                >
                  <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
                  <span>{copied ? 'คัดลอกแล้ว!' : 'คัดลอกลิงก์'}</span>
                </button>
                <button
                  onClick={() => setGeneratedLink('')}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-md"
                >
                  ล้าง
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={generateOneTimeLink}
              disabled={linkLoading}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md"
            >
              <SafeIcon icon={FiLink} className="w-4 h-4" />
              <span>{linkLoading ? 'กำลังสร้าง...' : 'สร้างลิงก์เข้าสู่ระบบ'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LineProfile;
